/**
 * Chart primitives.
 *
 * Ported from the design prototype, which drew raw SVG rather than pulling in a
 * charting library. Keeping that approach means the charts inherit the theme
 * tokens directly and there is no runtime to configure — but it does mean each
 * chart needs its real pixel width, because a stretched viewBox would scale the
 * text along with the geometry and leave the labels a different size on every card.
 * useMeasure supplies that width and re-renders on resize.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/* ------------------------------------------------------------------ measuring */

export function useMeasure() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const set = () => setWidth(node.clientWidth || node.parentElement?.clientWidth || 0);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  return [ref, width];
}

/* ------------------------------------------------------------------- tooltips */

const TipContext = { node: null };

function ensureTip() {
  if (TipContext.node && document.body.contains(TipContext.node)) return TipContext.node;
  const el = document.createElement('div');
  el.className = 'tip';
  document.body.appendChild(el);
  TipContext.node = el;
  return el;
}

export function useTip() {
  useEffect(() => () => { ensureTip().classList.remove('show'); }, []);

  const show = useCallback((event, html) => {
    const tip = ensureTip();
    tip.innerHTML = html;
    tip.classList.add('show');
    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    tip.style.left = `${Math.min(window.innerWidth - w - 12, Math.max(8, event.clientX + 14))}px`;
    tip.style.top = `${Math.max(8, event.clientY - h - 12)}px`;
  }, []);

  const hide = useCallback(() => { ensureTip().classList.remove('show'); }, []);
  return { show, hide };
}

/** Attaches tooltip handlers to any SVG shape. */
function tipProps(tip, html) {
  if (!html) return {};
  return {
    onMouseMove: (e) => tip.show(e, html),
    onMouseLeave: tip.hide,
  };
}

/* -------------------------------------------------------------------- scaling */

export function niceMax(value) {
  if (value <= 0) return 10;
  const p = 10 ** Math.floor(Math.log10(value));
  const n = value / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
}

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/* ---------------------------------------------------------------- line chart */

export function LineChart({
  series, labels, height = 220, leftGutter = 40, format = (v) => Number(v).toLocaleString('en-IN'),
  dots = true, xTicks = 8, min = 0,
}) {
  const [ref, W] = useMeasure();
  const tip = useTip();

  if (!series?.length || !labels?.length) return <div ref={ref} className="chart-host" />;
  if (!W) return <div ref={ref} className="chart-host" style={{ height }} />;

  const H = height;
  const P = { t: 14, r: 14, b: 26, l: leftGutter };
  const maxV = niceMax(Math.max(...series.flatMap((s) => s.data), min));
  const n = labels.length;
  const iw = W - P.l - P.r;
  const ih = H - P.t - P.b;
  const X = (i) => P.l + (n === 1 ? iw / 2 : (i * iw) / (n - 1));
  const Y = (v) => P.t + ih - (v / maxV) * ih;

  const step = Math.max(1, Math.ceil(n / xTicks));

  return (
    <div ref={ref} className="chart-host">
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img">
        {[0, 1, 2, 3, 4].map((k) => {
          const y = P.t + ih - (k * ih) / 4;
          return (
            <g key={k}>
              <line className="grid-l" x1={P.l} y1={y} x2={W - P.r} y2={y} />
              <text className="axis-l" x={P.l - 7} y={y + 3.5} textAnchor="end">
                {Math.round((maxV * k) / 4).toLocaleString('en-IN')}
              </text>
            </g>
          );
        })}

        {series.map((s) => {
          const pts = s.data.map((v, i) => [X(i), Y(v)]);
          const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
          return (
            <g key={s.name}>
              {s.area && (
                <path d={`${d} L${X(n - 1)} ${P.t + ih} L${X(0)} ${P.t + ih} Z`} fill={s.color} opacity=".10" />
              )}
              <path d={d} fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              {dots && pts.map((p, i) => (
                <circle key={i} cx={p[0]} cy={p[1]} r="3.2" fill={s.color} stroke="var(--surface)" strokeWidth="2" />
              ))}
            </g>
          );
        })}

        {labels.map((l, i) => {
          const html = `<div><b>${esc(l)}</b></div>${series
            .map((s) => `<div><span class="tk">${esc(s.name)}</span>${esc(format(s.data[i]))}</div>`).join('')}`;
          return (
            <rect
              key={i}
              x={X(i) - iw / (2 * Math.max(1, n - 1))}
              y={P.t}
              width={iw / Math.max(1, n - 1)}
              height={ih}
              fill="transparent"
              {...tipProps(tip, html)}
            />
          );
        })}

        {labels.map((l, i) => ((i % step === 0 || i === n - 1)
          ? <text key={`x${i}`} className="axis-l" x={X(i)} y={H - 7} textAnchor="middle">{l}</text>
          : null))}

        <line className="axis-line" x1={P.l} y1={P.t + ih} x2={W - P.r} y2={P.t + ih} />
      </svg>
    </div>
  );
}

/* ----------------------------------------------------------------- bar chart */

export function BarChart({
  data, height = 230, leftGutter = 40, bottomGutter = 42, valueOnTop = false,
  format = (v) => Number(v).toLocaleString('en-IN'), showLabels = true, unit = '',
}) {
  const [ref, W] = useMeasure();
  const tip = useTip();

  if (!data?.length) return <div ref={ref} className="chart-host" />;
  if (!W) return <div ref={ref} className="chart-host" style={{ height }} />;

  const H = height;
  const P = { t: 14, r: 14, b: bottomGutter, l: leftGutter };
  const maxV = niceMax(Math.max(...data.map((d) => d.v)));
  const iw = W - P.l - P.r;
  const ih = H - P.t - P.b;
  const n = data.length;
  const bw = Math.max(6, iw / n - 8);

  return (
    <div ref={ref} className="chart-host">
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img">
        {[0, 1, 2, 3, 4].map((k) => {
          const y = P.t + ih - (k * ih) / 4;
          return (
            <g key={k}>
              <line className="grid-l" x1={P.l} y1={y} x2={W - P.r} y2={y} />
              <text className="axis-l" x={P.l - 7} y={y + 3.5} textAnchor="end">
                {Math.round((maxV * k) / 4).toLocaleString('en-IN')}
              </text>
            </g>
          );
        })}

        {data.map((d, i) => {
          const x = P.l + (i * iw) / n + (iw / n - bw) / 2;
          const h = Math.max(2, (d.v / maxV) * ih);
          const y = P.t + ih - h;
          const html = `<div><b>${esc(d.l)}</b></div><div>${esc(format(d.v))}${esc(unit)}</div>`;
          return (
            <g key={`${d.l}-${i}`}>
              <rect
                x={x.toFixed(1)} y={y.toFixed(1)} width={bw.toFixed(1)} height={h.toFixed(1)}
                rx="4" fill={d.c || 'var(--s1)'} {...tipProps(tip, html)}
              />
              {showLabels && (
                <text className="axis-l" x={(x + bw / 2).toFixed(1)} y={H - (bottomGutter > 32 ? 24 : 8)} textAnchor="middle">
                  {d.l}
                </text>
              )}
              {valueOnTop && (
                <text
                  className="axis-l" x={(x + bw / 2).toFixed(1)} y={(y - 5).toFixed(1)} textAnchor="middle"
                  style={{ fontWeight: 650, fill: 'var(--text-2)' }}
                >
                  {format(d.v)}
                </text>
              )}
            </g>
          );
        })}

        <line className="axis-line" x1={P.l} y1={P.t + ih} x2={W - P.r} y2={P.t + ih} />
      </svg>
    </div>
  );
}

/* --------------------------------------------------------------- stack chart */

export function StackChart({ labels, series, height = 240, leftGutter = 38 }) {
  const [ref, W] = useMeasure();
  const tip = useTip();

  if (!labels?.length || !series?.length) return <div ref={ref} className="chart-host" />;
  if (!W) return <div ref={ref} className="chart-host" style={{ height }} />;

  const H = height;
  const P = { t: 14, r: 14, b: 30, l: leftGutter };
  const totals = labels.map((_, i) => series.reduce((a, s) => a + (s.data[i] ?? 0), 0));
  const maxV = niceMax(Math.max(...totals));
  const iw = W - P.l - P.r;
  const ih = H - P.t - P.b;
  const n = labels.length;
  const bw = Math.max(8, iw / n - 10);

  return (
    <div ref={ref} className="chart-host">
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img">
        {[0, 1, 2, 3, 4].map((k) => {
          const y = P.t + ih - (k * ih) / 4;
          return (
            <g key={k}>
              <line className="grid-l" x1={P.l} y1={y} x2={W - P.r} y2={y} />
              <text className="axis-l" x={P.l - 7} y={y + 3.5} textAnchor="end">{Math.round((maxV * k) / 4)}</text>
            </g>
          );
        })}

        {labels.map((l, i) => {
          const x = P.l + (i * iw) / n + (iw / n - bw) / 2;
          let acc = 0;
          return (
            <g key={l}>
              {series.map((s, si) => {
                const v = s.data[i] ?? 0;
                if (v <= 0) return null;
                const h = (v / maxV) * ih;
                const y = P.t + ih - ((acc + v) / maxV) * ih;
                acc += v;
                const isTop = si === series.length - 1 || series.slice(si + 1).every((z) => (z.data[i] ?? 0) <= 0);
                const html = `<div><b>${esc(l)}</b></div><div><span class="tk">${esc(s.name)}</span>${v}</div>`;
                return (
                  <rect
                    key={s.name}
                    x={x.toFixed(1)} y={y.toFixed(1)} width={bw.toFixed(1)}
                    height={Math.max(1, h - 2).toFixed(1)} rx={isTop ? 4 : 0}
                    fill={s.color} {...tipProps(tip, html)}
                  />
                );
              })}
              <text className="axis-l" x={(x + bw / 2).toFixed(1)} y={H - 9} textAnchor="middle">{l}</text>
            </g>
          );
        })}

        <line className="axis-line" x1={P.l} y1={P.t + ih} x2={W - P.r} y2={P.t + ih} />
      </svg>
    </div>
  );
}

/* --------------------------------------------------------------------- donut */

export function Donut({ data, size = 168, stroke = 22, middle, middleLabel = 'Total' }) {
  const tip = useTip();
  const r = (size - stroke) / 2;
  const c = size / 2;
  const total = data.reduce((a, d) => a + d.v, 0) || 1;
  let acc = 0;

  return (
    <div className="donut-c" style={{ width: size, height: size }}>
      <svg className="chart" viewBox={`0 0 ${size} ${size}`} style={{ width: size, height: size }} role="img">
        {data.map((d) => {
          const frac = d.v / total;
          if (frac <= 0) return null;
          const a0 = acc * 2 * Math.PI - Math.PI / 2;
          acc += frac;
          const a1 = acc * 2 * Math.PI - Math.PI / 2 - 0.018;
          const large = frac > 0.5 ? 1 : 0;
          const x0 = c + r * Math.cos(a0);
          const y0 = c + r * Math.sin(a0);
          const x1 = c + r * Math.cos(a1);
          const y1 = c + r * Math.sin(a1);
          const html = `<div><b>${esc(d.l)}</b></div><div>${d.v} · ${(frac * 100).toFixed(1)}%</div>`;
          return (
            <path
              key={d.l}
              d={`M${x0.toFixed(2)} ${y0.toFixed(2)} A${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`}
              fill="none" stroke={d.c} strokeWidth={stroke} strokeLinecap="butt"
              {...tipProps(tip, html)}
            />
          );
        })}
      </svg>
      <div className="donut-mid">
        <div className="dv">{middle !== undefined ? middle : total}</div>
        <div className="dl">{middleLabel}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ sparkline */

export function Spark({ data, color = 'var(--s1)', width = 120, height = 34 }) {
  if (!data?.length || data.length < 2) return null;
  const mx = Math.max(...data);
  const mn = Math.min(...data);
  const range = mx - mn || 1;
  const pts = data.map((v, i) => [(i * width) / (data.length - 1), height - ((v - mn) / range) * (height - 6) - 3]);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  return (
    <svg className="spark-svg" style={{ width, height }} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={`${d} L${width} ${height} L0 ${height} Z`} fill={color} opacity=".10" />
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/* -------------------------------------------------------------- horizontal bars */

export function HBars({
  rows,
  format = (v) => Number(v).toLocaleString('en-IN'),
  showTotal = false,
  totalLabel = 'Total',
}) {
  const tip = useTip();
  const max = Math.max(...rows.map((r) => r.v), 0) || 1;
  const total = rows.reduce((sum, r) => sum + (Number(r.v) || 0), 0);
  return (
    <>
      {rows.map((r) => (
        <div className="bar-h" key={r.l}>
          <div className="bl" title={r.l}>{r.l}</div>
          <div className="bt">
            <span
              style={{ width: `${((r.v / max) * 100).toFixed(1)}%`, background: r.c || 'var(--s1)' }}
              onMouseMove={(e) => tip.show(e, `<div><b>${esc(r.l)}</b></div><div>${esc(format(r.v))}</div>`)}
              onMouseLeave={tip.hide}
            />
          </div>
          <div className="bv">{format(r.v)}</div>
        </div>
      ))}
      {showTotal && rows.length > 0 && (
        <div className="bar-h bar-h-total">
          <div className="bl">{totalLabel}</div>
          <div className="bt" aria-hidden="true" />
          <div className="bv">{format(total)}</div>
        </div>
      )}
    </>
  );
}

/** The eight-step categorical palette, fixed for everyone so shared reports match. */
export const SERIES = ['var(--s1)', 'var(--s2)', 'var(--s3)', 'var(--s4)', 'var(--s5)', 'var(--s6)', 'var(--s7)', 'var(--s8)'];
export const seriesColor = (i) => SERIES[i % SERIES.length];
/** Departments and leave types carry their own token from the database. */
export const token = (t) => (t ? `var(--${t})` : 'var(--s1)');

export function Legend({ items }) {
  return (
    <div className="legend">
      {items.map((it) => (
        <span key={it.label}>
          {it.color && <i style={{ background: it.color }} />}
          {it.label}
        </span>
      ))}
    </div>
  );
}
